import type { Inputs } from './types';

export type PresetLevel = 'low' | 'medium' | 'high' | 'custom';

/** Nominal annual growth, net of fees. Tune these in one place. */
export const GROWTH_PRESETS = { low: 0.03, medium: 0.05, high: 0.07 } as const;

/** Annual inflation. */
export const INFLATION_PRESETS = { low: 0.02, medium: 0.03, high: 0.04 } as const;

export interface Currency {
  code: string;
  label: string;
  /** Drives separators and symbol placement via Intl.NumberFormat. */
  locale: string;
}

export const CURRENCIES: readonly Currency[] = [
  { code: 'GBP', label: 'British pound', locale: 'en-GB' },
  { code: 'EUR', label: 'Euro', locale: 'de-DE' },
  { code: 'USD', label: 'US dollar', locale: 'en-US' },
  { code: 'PLN', label: 'Polish złoty', locale: 'pl-PL' },
  { code: 'CHF', label: 'Swiss franc', locale: 'de-CH' },
  { code: 'SEK', label: 'Swedish krona', locale: 'sv-SE' },
  { code: 'NOK', label: 'Norwegian krone', locale: 'nb-NO' },
  { code: 'DKK', label: 'Danish krone', locale: 'da-DK' },
  { code: 'CAD', label: 'Canadian dollar', locale: 'en-CA' },
  { code: 'AUD', label: 'Australian dollar', locale: 'en-AU' },
  { code: 'JPY', label: 'Japanese yen', locale: 'ja-JP' },
];

export const DEFAULT_INPUTS: Inputs = {
  currency: 'GBP',
  householdType: 'individual',
  currentAge: 30,
  retirementAge: 67,
  currentPot: 0,
  // Build balances, so its `years` is ignored. Coast and Top up start empty:
  // assume you're working as you are now until you say otherwise.
  stages: [
    { years: 0, annualContribution: 0, growsWithInflation: false },
    { years: 0, annualContribution: 0, growsWithInflation: false },
    { years: 0, annualContribution: 0, growsWithInflation: false },
  ],
  growthRate: GROWTH_PRESETS.medium,
  inflationRate: INFLATION_PRESETS.medium,
  withdrawalRate: 0.04,
};

/** Which preset a rate corresponds to, so the UI can re-select the right radio on load. */
export function levelFor(
  rate: number,
  presets: Record<'low' | 'medium' | 'high', number>,
): PresetLevel {
  for (const level of ['low', 'medium', 'high'] as const) {
    if (Math.abs(presets[level] - rate) < 1e-9) return level;
  }
  return 'custom';
}
